import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { Router, UrlSerializer } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { flatMap } from "rxjs/operators";
import { environment } from "~env";
import { PaymentErrorDialog } from "../dialog/payment-error/payment-error-dialog";

export interface PaymentTransalation {
  pan_label: string;
  exp_label: string;
  cvd_label?: string;
}

export type PaymentVariant = "default" | "modern";

@Component({
    selector: "odq-payment",
    templateUrl: "./payment.component.html",
    styleUrls: ["./payment.component.scss"],
    standalone: false
})
export class PaymentComponent implements OnInit, AfterViewInit {
  @Output() dataKeyCreated = new EventEmitter();
  @Input() monerisStoreId: string;
  @Input() variant: PaymentVariant = "default";
  @Input() submitLabel: string;
  @Input() canSubmit = true;

  @ViewChild("monerisFrame", { static: false }) monerisFrame: ElementRef;
  @ViewChild("monerisResponse", { static: false }) monerisResponse: ElementRef;
  monerisUrl: any = "";

  disabledSubmit = true;
  canCreateDataKey = true;

  constructor(
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private serializer: UrlSerializer,
    private router: Router,
    private translate: TranslateService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener("window:message", ["$event"])
  handlePostMessage(e: MessageEvent) {
    if (
      e.origin === environment.shoppingCart.monerisOriginUrl 
      && e.isTrusted // True if the event was triggered by a real user action (click, keypress, etc.)
      && this.disabledSubmit // Only listens if the submit button was clicked
    ) {
      let respData;
      try {
        respData = JSON.parse(e?.data);
      } catch (error) {
        console.error("Moneris data json parse error : ", error);
        return;
      }

      if (respData && respData.dataKey) {
        if (this.canCreateDataKey) {
          this.monerisFrame.nativeElement.remove();
          // this.openErrorDialog(respData.responseCode, respData.dataKey);
          this.onDataKeyCreated({
            dataKey: respData.dataKey,
            bin: respData.bin,
          });
        }
        return;
      }
      this.openErrorDialog(respData.responseCode, respData.errorMessage);
    }
  }

  // DO NOT REMOVE, can be useful to test Moneris messages behaviour
  simulateMonerisMessage() {
    let data = {
      dataKey: "dataKey",
    };
    let e = {
      ...new MessageEvent(""),
      origin: environment.shoppingCart.monerisOriginUrl,
      data: JSON.stringify(data),
    };
    // console.log('e: ', e);
    this.handlePostMessage(e);
    this.handlePostMessage(e);
    this.handlePostMessage(e);
  }

  openErrorDialog(errorCode: string, errorMessage: string) {
    const dialogRef = this.dialog.open(PaymentErrorDialog, {
      minWidth: "200px",
      data: { code: errorCode, message: errorMessage },
    });

    this.disabledSubmit = false;
  }

  get isModern(): boolean {
    return this.variant === "modern";
  }

  ngOnInit() {
    const paymentTranslation = this.getPaymentTranslation();
    const baseMonerisUrl = environment.shoppingCart.monerisBaseUrl;
    const queryParams = this.isModern
      ? this.getModernQueryParams(paymentTranslation)
      : this.getDefaultQueryParams(paymentTranslation);
    const tree = this.router.createUrlTree([""], { queryParams });
    const queryArgs = this.serializer.serialize(tree).substr(1);
    this.monerisUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${baseMonerisUrl}${queryArgs}`
    );
  }

  private getDefaultQueryParams(paymentTranslation: PaymentTransalation) {
    return {
      id: this.monerisStoreId,
      display_labels: "1",
      enable_cvd: "1",
      enable_exp: "1",
      css_body: "background:transparent;",
      css_textbox: `height: 30px;
                    border: 1px solid #596a78; 
                    border-radius: 2px;
                    width: 100%;
                    margin-bottom: 10px;
                    outline-style: none;`,
      css_input_label: `font-family: sans-serif;
                        color: #596a78;`,
      css_textbox_pan: "",
      css_label_pan: "",
      css_textbox_exp: " width: 30%;",
      css_label_exp: "",
      css_textbox_cvd: `width: 30%;
                        position: absolute;
                        top: 78px;
                        right: 0;`,
      css_label_cvd: `position: absolute;
                      top: 60px;
                      right: 57px;`,
      pan_label: this.encode(paymentTranslation.pan_label),
      exp_label: this.encode(paymentTranslation.exp_label),
    };
  }

  private getModernQueryParams(paymentTranslation: PaymentTransalation) {
    return {
      id: this.monerisStoreId,
      display_labels: "1",
      enable_cvd: "1",
      enable_exp: "1",
      enable_exp_formatting: "1",
      css_body: "background:transparent;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);" +
        "column-gap:16px;align-items:end;font-family:Arial,sans-serif;",
      css_textbox: "box-sizing:border-box;width:100%;height:40px;margin:4px 0 14px;padding:0 12px;" +
        "border:1px solid #b0bec5;border-radius:4px;font-size:14px;color:#263238;outline-style:none;",
      css_input_label: "font-size:14px;font-weight:bold;color:#455a64;",
      css_label_pan: "grid-column:1/-1;grid-row:1;",
      css_textbox_pan: "grid-column:1/-1;grid-row:2;",
      css_label_exp: "grid-column:1;grid-row:3;",
      css_textbox_exp: "grid-column:1;grid-row:4;",
      css_label_cvd: "grid-column:2;grid-row:3;",
      css_textbox_cvd: "grid-column:2;grid-row:4;",
      pan_label: this.encodeSpecialChars(paymentTranslation.pan_label),
      exp_label: this.encodeSpecialChars(paymentTranslation.exp_label),
      cvd_label: this.encodeSpecialChars(paymentTranslation.cvd_label),
    };
  }

  /*
  The iframe onload event will be triggered twice in webkit browsers ( safari/chrome ), if you attach the onload event BEFORE the iframe is appended to the body.
  */
  ngAfterViewInit() {
    this.monerisFrame.nativeElement.addEventListener(
      "load",
      this.monerisFrameLoaded.bind(this)
    );
  }

  doMonerisSubmit() {
    this.disabledSubmit = true;
    const monFrameRef = this.monerisFrame.nativeElement.contentWindow;
    monFrameRef.postMessage("", environment.shoppingCart.monerisBaseUrl);
    return false;
  }

  monerisFrameLoaded(event) {
    this.disabledSubmit = false;
    this.cdr.detectChanges();
  }

  onDataKeyCreated(dataKey: any) {
    this.dataKeyCreated.emit(dataKey);
    this.canCreateDataKey = false;
  }

  /**
   * Converts a string to its html characters completely.
   *
   * @param {String} str String with unescaped HTML characters
   **/
  private encode(str) {
    const buf = [];

    if (!str) {
      return;
    }
    for (let i = str.length - 1; i >= 0; i--) {
      buf.unshift(["&#", str[i].charCodeAt(), ";"].join(""));
    }

    return buf.join("");
  }

  private encodeSpecialChars(str: string): string {
    return str?.replace(/[^A-Za-z0-9 ]/g, (char) => `&#${char.charCodeAt(0)};`);
  }

  private getPaymentTranslation(): PaymentTransalation {
    const translation: PaymentTransalation = {
      pan_label: "",
      exp_label: "",
    };

    if (this.isModern) {
      this.translate
        .get([
          "shoppingCart.payment.modern.cardNumber",
          "shoppingCart.payment.modern.expirationDate",
          "shoppingCart.payment.modern.securityCode",
        ])
        .subscribe((res) => {
          translation.pan_label = res["shoppingCart.payment.modern.cardNumber"];
          translation.exp_label = res["shoppingCart.payment.modern.expirationDate"];
          translation.cvd_label = res["shoppingCart.payment.modern.securityCode"];
        });
      return translation;
    }

    this.translate
      .get("shoppingCart.payment.creditCardNumber")
      .pipe(
        flatMap((res) => {
          translation.pan_label = res;
          return this.translate.get("shoppingCart.payment.expirationDate");
        })
      )
      .subscribe((res) => {
        translation.exp_label = res;
      });

    return translation;
  }
}
